import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { NOOR } from "@/constants/testIds/noor";
import { fadeUp, trans } from "@/components/noor/motion";
import StorySection from "@/components/noor/StorySection";

// 150-word brand manifesto — focused on transformation from earth to glass.
const MANIFESTO = `
Long before NOOR had a name, it had a stillness. A field of agave azul in the highlands
of Jalisco, waiting eight summers for the sun to finish what the earth began. We do not
hurry that stillness. We do not blend it. We do not bend it to a market.

When the agave is ready, six bottles are released to the world. Each one is cradled in
crystal cut with the geometry of a cathedral, sealed beneath a crown of rose-gold filigree,
and signed by the hand that bottled it.

NOOR is not a tequila to be tasted. It is a tequila to be received — quietly, like light
through a doorway in a house you have always known but never entered.

This is the architecture of patience. This is the discipline of one harvest.
This is the only NOOR there will ever be.
`.trim();

// Pre-split once so React keys stay stable across renders and we never re-split
// on every render. Each paragraph carries its own slug-style key.
const MANIFESTO_PARAGRAPHS = MANIFESTO.split("\n\n").map((text, index) => ({
  key: `manifesto-${index}-${text.slice(0, 24).replace(/\s+/g, "-")}`,
  text,
  isLead: index === 0,
}));

// Hoisted style objects — module-scope = stable identity across renders.
const STYLE_PAGE_BG = { background: "var(--noor-obsidian)" };
const STYLE_FACET = { opacity: 0.5 };
const STYLE_BACK_LINK = {
  color: "var(--noor-vellum)",
  letterSpacing: "0.28em",
  textTransform: "uppercase",
};
const STYLE_TAGLINE = { color: "var(--noor-vellum)", letterSpacing: "0.22em" };
const STYLE_HEADLINE = {
  color: "var(--noor-bone)",
  fontSize: "clamp(2.6rem, 5.4vw, 5.8rem)",
};
const STYLE_HEADLINE_ACCENT = { color: "var(--noor-rosegold)" };
const STYLE_SIGN_RULE = { background: "var(--noor-rosegold)", opacity: 0.6 };
const STYLE_SIGN_LABEL = {
  color: "var(--noor-vellum)",
  letterSpacing: "0.32em",
  textTransform: "uppercase",
};

const TRANS_HEADLINE = trans(1);
const TRANS_BODY = trans(1.1, 0.2);
const TRANS_CTAS = trans(0.9, 0.5);

function ManifestoBackLink() {
  return (
    <Link
      to="/"
      className="inline-flex items-center gap-2 font-mono text-xs"
      style={STYLE_BACK_LINK}
    >
      <ArrowLeft size={14} />
      Back to the house
    </Link>
  );
}

function ManifestoAside() {
  return (
    <header className="col-span-12 lg:col-span-3">
      <p className="overline mb-6">II · Manifesto</p>
      <span className="ornament" />
      <p className="mt-8 font-mono text-[10px] max-w-[160px] leading-relaxed" style={STYLE_TAGLINE}>
        From the earth<br />into the glass.
      </p>
    </header>
  );
}

function ManifestoHeadline() {
  return (
    <motion.h1
      {...fadeUp}
      transition={TRANS_HEADLINE}
      className="font-display leading-[0.95] tracking-[-0.035em]"
      style={STYLE_HEADLINE}
    >
      The architecture<br />
      of <em className="italic" style={STYLE_HEADLINE_ACCENT}>patience</em>.
    </motion.h1>
  );
}

function ManifestoBody() {
  return (
    <motion.div
      data-testid={NOOR.manifestoBody}
      {...fadeUp}
      transition={TRANS_BODY}
      className="mt-16 max-w-2xl"
    >
      {MANIFESTO_PARAGRAPHS.map((para) => (
        <p
          key={para.key}
          className="font-display-light text-xl md:text-2xl leading-[1.55] mb-10"
          style={{ color: para.isLead ? "var(--noor-bone)" : "var(--noor-vellum)" }}
        >
          {para.text}
        </p>
      ))}
    </motion.div>
  );
}

function ManifestoCTAs() {
  return (
    <motion.div
      {...fadeUp}
      transition={TRANS_CTAS}
      className="mt-16 flex flex-col sm:flex-row items-start gap-4"
    >
      <Link to="/inner-circle" data-testid={NOOR.manifestoCtaCircle} className="btn-solid">
        Enter the Inner Circle
        <ArrowRight size={14} />
      </Link>
      <Link to="/" data-testid={NOOR.manifestoCtaHome} className="btn-ghost">
        Return to the house
      </Link>
    </motion.div>
  );
}

function ManifestoSignature() {
  return (
    <div className="mt-24 flex items-center gap-6">
      <span className="w-12 h-px" style={STYLE_SIGN_RULE} />
      <span className="font-mono text-[10px]" style={STYLE_SIGN_LABEL}>
        Signed · The Keepers of NOOR
      </span>
    </div>
  );
}

export default function Manifesto() {
  return (
    <main
      data-testid={NOOR.manifestoPage}
      className="min-h-screen pt-24 pb-24 relative overflow-hidden"
      style={STYLE_PAGE_BG}
    >
      <div className="grain-overlay" />
      <div aria-hidden className="absolute inset-0 pointer-events-none facet-bg-diamond" style={STYLE_FACET} />
      <div className="relative max-w-[1480px] mx-auto px-6 md:px-10">
        <ManifestoBackLink />
        <section className="grid grid-cols-12 gap-y-10 md:gap-10 mt-20 md:mt-28">
          <ManifestoAside />
          <div className="col-span-12 lg:col-span-8 lg:col-start-5">
            <ManifestoHeadline />
            <div className="mt-16">
              <StorySection testId="manifesto-story" align="left" />
            </div>
            <ManifestoBody />
            <ManifestoCTAs />
            <ManifestoSignature />
          </div>
        </section>
      </div>
    </main>
  );
}
